import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Clock, ListTodo, UserX, FileText, CalendarX, Trophy, MessageSquare, Receipt } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import SnoozeButton from "./SnoozeButton.jsx";
import NotifPanel from "./NotifPanel.jsx";

// Zvoneček s notifikacemi trenéra (úkoly, neaktivní klienti, dokumenty, končící spolupráce).
// Vyřídit / odložit / archiv, perzistentní stav. Sdíleno: fialová hlavička trenér portálu
// (variant="dark") i horní lišta appky (variant="light").
const NOTIF_META = {
  ukol:      { Icon: ListTodo },
  dokument:  { Icon: FileText },
  neaktivni: { Icon: UserX },
  konec:     { Icon: Clock },
  vycerpano: { Icon: CalendarX },
  denik:     { Icon: Trophy },
  zminka:    { Icon: MessageSquare },
  vyplata:   { Icon: Receipt },
  faktura:   { Icon: Receipt },
};
const SEV_DOT = { high: "bg-rose-500", medium: "bg-amber-500", low: "bg-slate-300" };

// Načítání a akce trenérských notifikací. Sdílí ho TrenerBell i ManagerBell
// (30. 10. 2026, #186: manažer, který trénuje, má všechno v jednom zvonečku).
export function useTrenerNotifikace(activeTrener) {
  const [active, setActive] = useState([]);
  const [archive, setArchive] = useState([]);
  const [archiveCount, setArchiveCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const pilotQ = activeTrener ? `?pilot=${encodeURIComponent(activeTrener)}` : "";

  const loadActive = async () => {
    if (!activeTrener) return;
    try {
      const r = await apiFetch(`/api/trener-notifikace${pilotQ}`);
      const j = await r.json();
      setActive(j.active || []);
      setArchiveCount(j.archiveCount || 0);
    } catch { /* ticho */ }
  };
  const loadArchive = async () => {
    try {
      const r = await apiFetch(`/api/trener-notifikace${pilotQ}${pilotQ ? "&" : "?"}archive=1`);
      const j = await r.json();
      setArchive(j.items || []);
    } catch { /* ticho */ }
  };

  useEffect(() => { loadActive(); /* eslint-disable-next-line */ }, [activeTrener]);

  const act = async (notif_key, action, opts = {}, poAkci) => {
    setBusy(true);
    setActive((a) => a.filter((x) => x.notif_key !== notif_key));
    try {
      await apiFetch("/api/trener-notifikace", { method: "POST", body: JSON.stringify({ action, notif_key, ...opts }) });
      await loadActive();
      if (poAkci) await poAkci();
    } catch { await loadActive(); }
    finally { setBusy(false); }
  };

  return { active, archive, archiveCount, busy, loadActive, loadArchive, act };
}

// Jedna aktivní trenérská notifikace (Vyřídit, Odložit). `stitek` a `otevritText`
// používá společný zvoneček manažera, který trénuje.
export function TrenerNotifRadek({ n, busy, act, onOpen, stitek = null, otevritText = "" }) {
  const meta = NOTIF_META[n.typ] || NOTIF_META.ukol;
  return (
    <div className="px-3 py-3 sm:py-2.5 border-b border-slate-100 sm:border-slate-50 last:border-b-0 hover:bg-slate-50/60">
      <div className="flex items-start gap-2.5">
        <span className={`w-2.5 h-2.5 sm:w-2 sm:h-2 rounded-full mt-1.5 shrink-0 ${SEV_DOT[n.severity] || SEV_DOT.low}`} />
        <button onClick={() => onOpen(n.link)} className="min-w-0 flex-1 text-left">
          {stitek}
          <div className="text-[15px] sm:text-sm font-semibold text-slate-800 leading-snug">{n.title}</div>
          <div className="text-[13px] sm:text-xs text-slate-500 mt-0.5 leading-snug">{n.detail}</div>
        </button>
        <meta.Icon className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-2 pl-6 sm:pl-5">
        <button onClick={() => act(n.notif_key, "done")} disabled={busy} className="inline-flex items-center gap-1 text-sm sm:text-xs font-semibold px-3 py-2 sm:px-2.5 sm:py-1 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 active:bg-emerald-200 disabled:opacity-50 transition-colors">
          <Check className="w-4 h-4 sm:w-3.5 sm:h-3.5" /> Vyřídit
        </button>
        <SnoozeButton onPick={(o) => act(n.notif_key, "snooze", o)} disabled={busy} />
        {otevritText && n.link && (
          <button onClick={() => onOpen(n.link)} className="inline-flex items-center gap-1 text-sm sm:text-xs font-semibold px-3 py-2 sm:px-2.5 sm:py-1 rounded-xl border border-amber-200 text-amber-800 hover:bg-amber-50 active:bg-amber-100 transition-colors">
            {otevritText}
          </button>
        )}
      </div>
    </div>
  );
}

export default function TrenerBell({ activeTrener, variant = "dark" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("aktivni");
  const { active, archive, archiveCount, busy, loadActive, loadArchive, act: actZaklad } = useTrenerNotifikace(activeTrener);

  useEffect(() => { if (open && tab === "archiv") loadArchive(); /* eslint-disable-next-line */ }, [open, tab]);

  const act = (notif_key, action, opts = {}) => actZaklad(notif_key, action, opts, tab === "archiv" ? loadArchive : null);

  const goTo = (link) => { setOpen(false); if (link) navigate(link); };
  const count = active.length;

  const triggerCls = variant === "light"
    ? "relative flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl hover:bg-slate-100"
    : "relative w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 border border-white/20 flex items-center justify-center transition-colors";
  const iconCls = variant === "light"
    ? `h-6 w-6 ${count > 0 ? "text-red-600" : "text-slate-600"}`
    : "w-5 h-5 text-white";
  const badgeRing = variant === "light" ? "ring-white" : "ring-brand-800";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setTab("aktivni"); loadActive(); }}
        className={triggerCls}
        title="Notifikace"
        aria-label="Notifikace"
      >
        <Bell className={iconCls} />
        {count > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ${badgeRing} tabular-nums`}>
            {count}
          </span>
        )}
      </button>

      {/* Panel: mobil = přes celou obrazovku, desktop = dropdown (NotifPanel). */}
      <NotifPanel open={open} onClose={() => setOpen(false)} count={count} iconClass="text-brand-600">
        {/* 4.9.2026: přepínač na celou šířku, stejný jako widget notifikací
            na trenérském Přehledu. */}
        <div className="px-4 pb-3 pt-3 sm:px-3 sm:pb-2 sm:pt-2">
          <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1">
            <button
              onClick={() => setTab("aktivni")}
              className={`min-h-[44px] min-w-0 flex-1 rounded-xl text-sm font-semibold transition-colors sm:min-h-[32px] sm:text-xs ${tab === "aktivni" ? "bg-white text-brand-deep shadow-sm" : "text-slate-500"}`}
            >
              Aktivní{count > 0 ? ` ${count}` : ""}
            </button>
            <button
              onClick={() => setTab("archiv")}
              className={`min-h-[44px] min-w-0 flex-1 rounded-xl text-sm font-semibold transition-colors sm:min-h-[32px] sm:text-xs ${tab === "archiv" ? "bg-white text-brand-deep shadow-sm" : "text-slate-500"}`}
            >
              Archiv{archiveCount > 0 ? ` ${archiveCount}` : ""}
            </button>
          </div>
        </div>

        <div className="sm:max-h-[60vh] sm:overflow-y-auto overscroll-contain py-1">
          {tab === "aktivni" ? (
            active.length === 0 ? (
              <div className="px-4 py-10 sm:py-8 text-center text-sm text-slate-400">
                <Check className="w-7 h-7 text-emerald-400 mx-auto mb-2" />
                Žádné notifikace. Vše vyřízeno.
              </div>
            ) : active.map((n) => (
              <TrenerNotifRadek key={n.notif_key} n={n} busy={busy} act={act} onOpen={goTo} />
            ))
          ) : (
            archive.length === 0 ? (
              <div className="px-4 py-10 sm:py-8 text-center text-sm text-slate-400">Archiv je prázdný.</div>
            ) : archive.map((n) => {
              const meta = NOTIF_META[n.typ] || NOTIF_META.ukol;
              return (
                <div key={n.notif_key} className="px-3 py-3 sm:py-2.5 border-b border-slate-100 sm:border-slate-50 last:border-b-0 opacity-75">
                  <div className="flex items-start gap-2.5">
                    <meta.Icon className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                    <button onClick={() => goTo(n.link)} className="min-w-0 flex-1 text-left">
                      <div className="text-[15px] sm:text-sm font-medium text-slate-600 leading-snug line-through decoration-slate-300">{n.title}</div>
                      <div className="text-xs sm:text-[11px] text-slate-400 mt-0.5">{n._stav === "snoozed" ? `Odloženo do ${n._snooze_until}` : "Vyřízeno"}</div>
                    </button>
                    <button onClick={() => act(n.notif_key, "reopen")} disabled={busy} className="text-sm sm:text-xs font-semibold text-brand-600 hover:text-brand-800 shrink-0 px-2 py-1 -mr-1">Vrátit</button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </NotifPanel>
    </div>
  );
}
